/**
 * Call History Hooks (Application)
 *
 * React Query hooks for call history search.
 * Uses the repository pattern from clean architecture.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';

import { callHistorySearchRepository } from '../../infrastructure/repositories';

import type { CallHistorySearchRequest } from '../../domain/types/requests';

// ============================================================================
// Query Keys Factory Pattern
// ============================================================================

export const callHistoryKeys = {
  all: ['call-history'] as const,
  search: (request?: CallHistorySearchRequest) => [...callHistoryKeys.all, 'search', request] as const,
  options: () => [...callHistoryKeys.all, 'options'] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to search call history with filters, sorting, and pagination
 */
export function useCallHistorySearch(request: CallHistorySearchRequest) {
  return useQuery({
    queryKey: callHistoryKeys.search(request),
    queryFn: () => callHistorySearchRepository.searchCallHistory(request),
    staleTime: 30 * 1000, // 30 seconds
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to fetch search options (columns and presets)
 */
export function useCallHistorySearchOptions() {
  return useQuery({
    queryKey: callHistoryKeys.options(),
    queryFn: () => callHistorySearchRepository.getSearchOptions(),
    staleTime: 10 * 60 * 1000, // 10 minutes - options rarely change
  });
}
